import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { Table } from '@/components';
import { vendorOrderApi } from '@/services/vendorOrderApi';
import { Order } from '@/types/order';

type StatusFilter = 'all' | 'awaiting_payment' | 'pending' | 'confirmed' | 'preparing' | 'on_the_way' | 'delivered' | 'cancelled';

const STATUS_LABELS: Record<string, string> = {
  awaiting_payment: 'Ödeme Bekleniyor',
  pending: 'Beklemede',
  confirmed: 'Onaylandı',
  preparing: 'Hazırlanıyor',
  on_the_way: 'Kurye Yolda',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal',
};

const STATUS_COLORS: Record<string, string> = {
  awaiting_payment: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  confirmed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  preparing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  on_the_way: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const getDateString = (daysOffset = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0];
};

const formatDate = (value: unknown) => {
  const parsed = value ? new Date(value as string | number | Date) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleDateString('tr-TR') : '-';
};

const VendorOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [startDate, setStartDate] = useState<string>(getDateString(-2));
  const [endDate, setEndDate] = useState<string>(getDateString(1));
  const [searchQuery, setSearchQuery] = useState<string>('');

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await vendorOrderApi.getOrders({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        searchQuery: searchQuery || undefined,
        limit: 100,
      });
      setOrders(response.orders || []);
    } catch (error: any) {
      console.error('Vendor siparişleri yüklenemedi:', error);
      toast.error(error.message || 'Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOrders();
  }, [statusFilter, startDate, endDate, searchQuery]);

  const tableData = orders.map((order) => ({
    id: order.id,
    orderNo: order.orderNo || '-',
    recipient: order.recipientInfo?.fullName || 'Misafir',
    sender: order.senderInfo?.name || '-',
    deliveryDate: order.items?.[0]?.deliveryDate ? formatDate(order.items[0].deliveryDate) : '-',
    total: `₺${(order.totalPrice || 0).toFixed(2)}`,
    status: STATUS_LABELS[order.status] || order.status,
    statusRaw: order.status,
    date: formatDate(order.createdAt),
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-dark dark:text-light">Siparişlerim</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Size atanmış siparişleri yönetin</p>
      </div>

      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Ara</label>
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Gönderen, alıcı, e-posta veya telefon ile ara"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Durum</label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              className="input-field"
            >
              <option value="all">Tümü</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Başlangıç</label>
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Bitiş</label>
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className="input-field" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin text-4xl">⏳</div>
        </div>
      ) : (
        <Table
          headers={['Sipariş No', 'Alıcı', 'Gönderen', 'Teslimat', 'Toplam', 'Durum', 'Tarih']}
          data={tableData}
          renderCell={(item, header) => {
            if (header === 'Durum') {
              return (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[item.statusRaw] || 'bg-gray-100 text-gray-700'}`}>
                  {item.status}
                </span>
              );
            }

            const headerMap: Record<string, string> = {
              'Sipariş No': 'orderNo',
              'Alıcı': 'recipient',
              'Gönderen': 'sender',
              'Teslimat': 'deliveryDate',
              'Toplam': 'total',
              'Tarih': 'date',
            };

            const key = headerMap[header];
            return key ? item[key as keyof typeof item] : '-';
          }}
          actions={(item) => (
            <button
              onClick={() => navigate(`/vendor/orders/${item.id}`)}
              className="p-2 text-primary hover:bg-blue-100 dark:hover:bg-blue-900 rounded transition-smooth"
            >
              <Eye size={16} />
            </button>
          )}
        />
      )}
    </div>
  );
};

export default VendorOrdersPage;